import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowDownLeft, Copy, Check } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import { IEVMTransaction } from 'src/types';
import { useAttributionForAddress } from 'src/hooks/attributions/useAttributionForAddress';
import { useAllAttributions } from 'src/hooks/attributions/useAllAttributions';
import { useAddressAndChainParams } from 'src/hooks/routing/useAddressAndChainParams';
import { LockedAttributionHoverCard } from 'src/components/Attribution/LockedAttributionHoverCard';

interface TransactionAddressesProps {
  transaction: IEVMTransaction;
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
}

export const EVMTransactionAddresses: React.FC<TransactionAddressesProps> = ({
  transaction,
  copiedField,
  onCopy,
}) => {
  const { chain } = useAddressAndChainParams();
  useAllAttributions(chain, [
    { _from: transaction._from, _to: transaction._to },
  ]);

  const { attribution: fromAttribution } = useAttributionForAddress(
    chain,
    transaction._from,
  );
  const { attribution: toAttribution } = useAttributionForAddress(
    chain,
    transaction._to,
  );

  const fromEntity = fromAttribution?.entity;
  const toEntity = toAttribution?.entity;

  return (
    <div className="rounded-lg bg-gray-50 p-6">
      <h2 className="mb-4 text-lg font-semibold">Addresses</h2>
      <div className="space-y-2">
        <div className="flex items-start gap-4">
          <div className="flex min-w-[200px] items-center gap-2">
            <ArrowUpRight className="h-5 w-5 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-500">From</h2>
          </div>
          <div className="flex flex-1 items-center gap-2">
            {fromEntity && (
              <LockedAttributionHoverCard
                attributionValue={fromEntity}
                attributionType="entity"
                iconSize="sm"
                fallback={<Badge variant="primary">{fromEntity}</Badge>}
              />
            )}

            <Link
              to={`/${chain}/address/${transaction._from}`}
              className="text-md font-mono text-[#0584C3] hover:underline"
              title={transaction._from}
            >
              {transaction._from}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onCopy(transaction._from, 'from')}
              className="flex h-5 w-5 items-center justify-center hover:bg-transparent hover:text-[#0584C3]"
              title="Copy address"
            >
              {copiedField === 'from' ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="flex min-w-[200px] items-center gap-2">
            <ArrowDownLeft className="h-5 w-5 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-500">To</h2>
          </div>
          <div className="flex flex-1 items-center gap-2">
            {toEntity && (
              <LockedAttributionHoverCard
                attributionValue={toEntity}
                attributionType="entity"
                iconSize="sm"
                fallback={<Badge variant="primary">{toEntity}</Badge>}
              />
            )}

            <Link
              to={`/${chain}/address/${transaction._to}`}
              className="text-md font-mono text-[#0584C3] hover:underline"
              title={transaction._to}
            >
              {transaction._to}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onCopy(transaction._to, 'to')}
              className="flex h-5 w-5 items-center justify-center hover:bg-transparent hover:text-[#0584C3]"
              title="Copy address"
            >
              {copiedField === 'to' ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
